import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {
  Edit as EditIcon,
  ExpandMore as ExpandLessIcon,
  KeyboardArrowRight as ExpandMoreIcon,
  InboxOutlined,
} from '@mui/icons-material';
import {isLoaded} from 'Core/models/Async';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {SplitPart} from 'Quoting/models/DTOs/PremiumSplit';
import {QuoteStatus} from 'Quoting/models/enum';
import {VALUE_NOT_AVAILABLE, isDefined} from 'Quoting/utils/utilities';
import {NoOp} from 'Shared/utilities/IOUtilities';
import {List as ImmutableList} from 'immutable';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FormRowGrid} from '../../shared/GridRow';
import {QuoteFormElement} from '../../shared/QuoteFormElement';
import {CompensationTypeForm, getMaxCompValError} from './CompensationTypeForm';
import {ContractedCommissionsBlock} from './ContractedCommissions';
import {EditAgenciesCompensation} from './EditAgenciesCompensation';

const useAgencyStyles = makeStyles(() => ({
  tabPanel: {
    padding: '0.5rem 2rem',
  },
  labelText: {
    fontSize: '0.875rem',
    lineHeight: '1.05rem',
    color: '#474747',
  },
  labelValue: {
    fontSize: '0.875rem',
    lineHeight: '1.05rem',
    color: '#474747',
    fontWeight: 700,
    paddingTop: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    width: '100%',
  },
  accordion: {
    boxShadow: 'none',
    color: '#333333',
    '&::before': {
      display: 'none',
    },
  },
  accordionDetails: {
    display: 'block',
    width: '100%',
    padding: 0,
  },
  noRecordParent: {
    display: 'grid',
    placeItems: 'center',
    height: '15vh',
    textAlign: 'center',
    padding: '15px 5px',
  },
  techPartnersTitle: {
    fontSize: '1rem',
    lineHeight: '1.2rem',
    color: '#333333',
    marginLeft: '5px',
    fontWeight: 600,
    padding: '15px 0px',
  },
  alignRecordCenter: {
    width: '50vw',
    height: '100px',
  },
  noRotateIcon: {
    '& .MuiAccordionSummary-expandIcon': {
      transform: 'none !important',
    },
  },
  addEditCompensation: {
    textTransform: 'capitalize',
    '& span': {
      fontSize: '1rem',
    },
  },
}));

const AgencyAccordionSummary = ({
  expanded,
  handleChange,
  onEditClick,
  classes,
  splitPart,
  isEditable,
}: {
  expanded: boolean;
  handleChange: () => void;
  onEditClick: () => void;
  classes: any;
  splitPart: SplitPart;
  isEditable: boolean;
}) => (
  <AccordionSummary
    expandIcon={
      isEditable && (
        <Button onClick={onEditClick} color="primary" startIcon={<EditIcon />} className={classes.addEditCompensation}>
          Select Compensation Type
        </Button>
      )
    }
    id="panel1-header"
    className={classes.noRotateIcon}
    onClick={handleChange}
    aria-label="expand agency details"
  >
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={1}>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Grid>

      <Grid item xs={2}>
        <Typography variant="body2" className={classes.labelText}>
          Producer
        </Typography>
        <Typography
          variant="subtitle1"
          component="span"
          className={classes.labelValue}
          style={{color: '#0033A0'}}
          title={splitPart?.brokerName}
        >
          {splitPart?.brokerName}
        </Typography>
      </Grid>

      <Grid item xs={2}>
        <Typography variant="body2" className={classes.labelText}>
          Premium Split %
        </Typography>
        <Typography variant="subtitle1" component="span" className={classes.labelValue}>
          {splitPart?.premiumSplitPct * 100}%
        </Typography>
      </Grid>

      <Grid item xs={2}>
        <Typography variant="body2" className={classes.labelText}>
          Compensation Type
        </Typography>
        <Typography variant="subtitle1" component="span" className={classes.labelValue}>
          {splitPart?.premiumSplitDetails[0].compensationTypeCd}
        </Typography>
      </Grid>
    </Grid>
  </AccordionSummary>
);

const AgencyDetailSection = ({
  onEditClick,
  classes,
  splitPart,
  isCompVisible,
  isEditable,
  commissionStyle,
}: {
  onEditClick: () => void;
  classes: any;
  splitPart: SplitPart;
  isCompVisible: boolean;
  isEditable: boolean;
  commissionStyle: string;
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const handleChange = () => setExpanded(!expanded);

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      className={classes.accordion}
      TransitionProps={{unmountOnExit: true}}
    >
      <AgencyAccordionSummary
        expanded={expanded}
        handleChange={handleChange}
        onEditClick={onEditClick}
        classes={classes}
        splitPart={splitPart}
        isEditable={isEditable}
      />

      <AccordionDetails className={classes.accordionDetails}>
        <Typography className={classes.techPartnersTitle}>PRODUCER DETAILS</Typography>
        <Divider style={{width: '100%'}} />
        <Grid container spacing={2} style={{paddingTop: '10px'}}>
          <Grid item xs={3}>
            <Typography variant="body2" className={classes.labelText}>
              Producer
            </Typography>
            <Typography
              variant="subtitle1"
              component="span"
              className={classes.labelValue}
              title={splitPart?.brokerName}
            >
              {splitPart?.brokerName}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" className={classes.labelText}>
              Role
            </Typography>
            <Typography variant="subtitle1" component="span" className={classes.labelValue}>
              {splitPart?.premiumSplitDetails[0]?.broker.roleCd.replace(/([a-z])([A-Z])/g, `$1 $2`)}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" className={classes.labelText}>
              Premium Split %
            </Typography>
            <Typography variant="subtitle1" component="span" className={classes.labelValue}>
              {splitPart?.premiumSplitPct * 100}%
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" className={classes.labelText}>
              Compensation Type
            </Typography>
            <Typography variant="subtitle1" component="span" className={classes.labelValue}>
              {splitPart?.premiumSplitDetails[0].compensationTypeCd}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" className={classes.labelText}>
              Contract Number
            </Typography>
            <Typography variant="subtitle1" component="span" className={classes.labelValue}>
              {splitPart?.premiumSplitDetails?.[0]?.broker?.isContractAssigned
                ? splitPart?.premiumSplitDetails?.[0]?.broker?.contractCd
                : VALUE_NOT_AVAILABLE}
            </Typography>
          </Grid>
        </Grid>
        {commissionStyle === A360CommissionStyle.Group && (
          <>
            <Typography
              style={{
                fontSize: '0.9rem',
                lineHeight: '1.2rem',
                color: '#333333',
                marginLeft: '5px',
                fontWeight: 600,
                paddingTop: '20px',
              }}
            >
              COMPENSATION
            </Typography>
            <Divider style={{width: '100%'}} />
            {isCompVisible ? (
              <CompensationTypeForm disabled={true} isConfirmDisabled={NoOp} />
            ) : (
              <div className={classes.noRecordParent}>
                <InboxOutlined style={{color: '#D9D9D9', transform: 'scale(2)'}} />
                <br />
                <div className={classes.alignRecordCenter}>There are no items here</div>
              </div>
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const TechPartnerDetails: React.FC<{techFeePctValue: number}> = ({techFeePctValue}) => {
  const {decisionTableLevelResponse} = useSelector((state: ReducerState) => state.quoteActions);
  const classes = useAgencyStyles();
  return (
    <>
      <Typography className={classes.techPartnersTitle}>Tech Partner Details</Typography>
      <Grid item xs={4} sm={4} style={{padding: '8px'}}>
        <QuoteFormElement
          elementType="Text"
          id="techFeePct"
          label="Quoted Tech Fee %"
          required
          value={techFeePctValue}
          disabled
          error={techFeePctValue > decisionTableLevelResponse[0]?.maxCommissionLevel}
          helperText={
            techFeePctValue > decisionTableLevelResponse[0]?.maxCommissionLevel &&
            getMaxCompValError(decisionTableLevelResponse[0]?.maxCommissionLevel)
          }
        />
      </Grid>
    </>
  );
};

const LoadingDetails = () => {
  return (
    <>
      <Skeleton animation="pulse" width="100%" height={100} />
      <Skeleton animation="pulse" width="100%" />
      <Skeleton animation="pulse" width="100%" />
    </>
  );
};

export const ViewAgenciesCompensation = () => {
  const classes = useAgencyStyles();

  const {getQuoteFieldValue} = useQuoteField();

  const {compensationValue, premiumSplitSummary} = useSelector((state: ReducerState) => state.compensation);

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const noPlansAdded: boolean =
    !isDefined(getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')) ||
    (isDefined(getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')) &&
      getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')?.size === 0);

  const [isCompensationFormVisible, setIsCompensationFormVisible] = useState<boolean>(
    compensationValue.rate?.trim() !== '' || compensationValue.type?.trim() !== '' || !noPlansAdded
  );

  const handleIconClick = (isEdit: boolean, isExpand: boolean) => {
    setIsEditing(isEdit);
  };

  const commissionStyle = getQuoteFieldValue<string>('commissionStyle');

  const techFeePctValue = getQuoteFieldValue<number>('techPartners[0].techFeePct') ?? 0;

  const getPremiumDetails = (isEditable: boolean) =>
    isLoaded(premiumSplitSummary)
      ? premiumSplitSummary.Data?.splitParts.map(split => (
          <React.Fragment key={split?.premiumSplitPct}>
            <AgencyDetailSection
              onEditClick={() => handleIconClick(true, true)}
              classes={classes}
              splitPart={split}
              isCompVisible={isCompensationFormVisible}
              isEditable={isEditable}
              commissionStyle={commissionStyle}
            />
            <Divider style={{marginTop: '10px'}} />
          </React.Fragment>
        ))
      : LoadingDetails;

  useEffect(() => {
    const value = compensationValue.rate?.trim() !== '' || compensationValue.type !== '';
    setIsCompensationFormVisible(value);
  }, [compensationValue.rate, compensationValue.type, isDefined]);

  const row = [
    {
      rowProps: {key: 'agencies-row-1'},
      items: [
        {
          key: 'comission-style',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${getQuoteFieldValue('_modelName')}PolicySummary.commissionStyle`}
              id="comission-style"
              label="Commission Style"
              required
              value={commissionStyle ?? A360CommissionStyle.Group}
              disabled
            />
          ),
        },
      ],
    },
  ];

  return (
    <>
      {commissionStyle === A360CommissionStyle.Contracted && (
        <>
          <ContractedCommissionsBlock />
          <Divider style={{margin: '1rem'}} />
          {getPremiumDetails(false)}
        </>
      )}
      <Grid item xs={12} sm={12} lg={12} className={classes.tabPanel}>
        {commissionStyle !== A360CommissionStyle.Contracted && (
          <>
            <FormRowGrid rows={row} />
            <Typography className={classes.techPartnersTitle}>Producer Details</Typography>
            <Divider />
            {isEditing && isLoaded(premiumSplitSummary)
              ? premiumSplitSummary.Data?.splitParts.map(split => (
                  <EditAgenciesCompensation
                    key={split?.premiumSplitPct}
                    splitPart={split}
                    isCompensationFormVisible={isCompensationFormVisible}
                    onCancel={(isEdit: boolean, isExpand: boolean) => handleIconClick(isEdit, isExpand)}
                    onConfirm={() => handleIconClick(false, false)}
                    setCompensationFormVisible={(visible: boolean) => setIsCompensationFormVisible(visible)}
                  />
                ))
              : getPremiumDetails(
                  ![QuoteStatus.Proposed, QuoteStatus.ContractAccepted, QuoteStatus.Sold].includes(
                    getQuoteFieldValue<string>('state') as QuoteStatus
                  )
                )}
          </>
        )}
        <TechPartnerDetails techFeePctValue={techFeePctValue} />
      </Grid>
    </>
  );
};

const A360CommissionStyle = {
  Group: 'Group',
  Contracted: 'Contracted',
} as const;
